import { Injectable } from '@angular/core';
import { ICustomField } from 'src/app/features/settings/models/settings.models';
import { CustomFieldTypes } from 'src/app/features/settings/models/custom-fields.constants';

/** Raw value bag as served by the API, keyed by a definition's technical_name. */
export type ICustomFieldValues = { [key: string]: string | string[] | number | boolean | null };

/**
 * Single source of truth for rendering a custom field's label and value.
 *
 * Extracted from ObjectCustomFieldsViewComponent (which now delegates here) so
 * the same formatting applies wherever custom fields surface — the side panels,
 * and the order-lines table columns added by WB-7619.
 */
@Injectable({ providedIn: 'root' })
export class CustomFieldDisplayService {
  static readonly EMPTY_PLACEHOLDER = '--';

  /** Definition label for `language`, falling back to any filled translation, then the technical name. */
  label(definition: ICustomField, language: string = 'en'): string {
    return (
      definition?.label?.[language] ||
      Object.values(definition?.label ?? {}).find((value) => !!value) ||
      definition?.technical_name ||
      ''
    );
  }

  /** True when the value bag carries an entry for this definition at all (even an empty one). */
  hasValue(values: ICustomFieldValues | null | undefined, definition: ICustomField): boolean {
    if (!values) return false;
    return Object.prototype.hasOwnProperty.call(values, definition.technical_name);
  }

  value(values: ICustomFieldValues | null | undefined, definition: ICustomField): unknown {
    return values?.[definition.technical_name];
  }

  /**
   * Display text for a single-value field. Booleans return an i18n key
   * ('common.yes' / 'common.no') — callers must pipe the result through
   * `translate`.
   */
  text(
    values: ICustomFieldValues | null | undefined,
    definition: ICustomField,
    placeholder: string = CustomFieldDisplayService.EMPTY_PLACEHOLDER
  ): string {
    const raw = this.value(values, definition);
    if (raw === null || raw === undefined || raw === '') return placeholder;
    if (definition.field_type === CustomFieldTypes.BOOLEAN) return raw ? 'common.yes' : 'common.no';
    return String(raw);
  }

  /** Select values as a flat string array — single- and multi-select share one shape. */
  selectValues(values: ICustomFieldValues | null | undefined, definition: ICustomField): string[] {
    const raw = this.value(values, definition);
    if (Array.isArray(raw)) {
      return raw.filter((entry) => entry !== null && entry !== undefined).map((entry) => String(entry));
    }
    if (raw === null || raw === undefined || raw === '') return [];
    return [String(raw)];
  }

  isEmail(definition: ICustomField): boolean {
    return definition.field_type === CustomFieldTypes.EMAIL;
  }

  isUrl(definition: ICustomField): boolean {
    return definition.field_type === CustomFieldTypes.URL;
  }

  isSelect(definition: ICustomField): boolean {
    return (
      definition.field_type === CustomFieldTypes.SINGLE_SELECT ||
      definition.field_type === CustomFieldTypes.MULTI_SELECT
    );
  }

  /** href for email / url fields; empty string when there is nothing to link to. */
  href(values: ICustomFieldValues | null | undefined, definition: ICustomField): string {
    const raw = this.value(values, definition);
    const text = raw === null || raw === undefined ? '' : String(raw).trim();
    if (!text) return '';
    if (this.isEmail(definition)) return `mailto:${text}`;
    if (this.isUrl(definition)) {
      // ensure scheme for proper link navigation
      return /^https?:\/\//i.test(text) ? text : `https://${text}`;
    }
    return text;
  }
}
